import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  Copy,
  Building2,
  User,
  MapPin,
  Boxes,
  Activity,
  Edit3,
  CreditCard,
  CalendarClock,
  Clock3,
  FileText,
  ClipboardCheck,
  Phone,
  Link2,
  Users,
  Plus,
  Ban,
  CheckCircle2,
  KeyRound,
} from 'lucide-react';
import {
  Drawer,
  Avatar,
  Badge,
  Button,
  Table,
  TableContainer,
  PromptModal,
  Modal,
  ConfirmModal,
  TextInput,
  Select,
  CurrencyInput,
  ActionButton,
  MerchantEmployeeFields,
  emptyMerchantEmployee,
  merchantEmployeeFromDto,
  merchantEmployeeToPayload,
  validateMerchantEmployee,
  hasMerchantEmployeeErrors,
  isBlankMerchantEmployee,
  formatDocument,
  formatPhone,
} from '@kardume/ui';
import type { MerchantEmployeeFormValue } from '@kardume/ui';
import MerchantPartnershipSection from './MerchantPartnershipSection';
import { useNotification } from '@kardume/hooks';
import { backofficeApi, extractErrorMessage, formatCurrency, formatDate } from '@kardume/services';
import type {
  MerchantDto,
  MerchantStatus,
  MerchantSource,
  Tone,
  MerchantBillingAnalysisDto,
  MerchantAvailabilityAnalysisDto,
  MerchantAvailabilityCheckDto,
  SubscriptionDto,
  InvoiceDto,
  SubscriptionCreditDto,
  PlatformAuditEventDto,
  PlanDto,
  BillingPeriod,
  BusinessHourDto,
  BackofficeMerchantEmployeeDto,
  BackofficeMerchantAccessProfileOptionDto,
  MerchantEmployeeStatus,
  MerchantRole,
} from '@kardume/types';
import {
  MERCHANT_STATUS_LABELS,
  MERCHANT_ROLE_LABELS,
  MERCHANT_EMPLOYEE_STATUS_LABELS,
  MERCHANT_EMPLOYEE_ERROR_MESSAGES,
} from '@kardume/types';

/**
 * Rótulos das origens (card 231, §38.3).
 *
 * Em português e no vocabulário de quem opera: "equipe Kardume" é o que a
 * pessoa reconhece, não `BACKOFFICE`.
 */
const MERCHANT_SOURCE_LABELS: Record<MerchantSource, string> = {
  BACKOFFICE: 'Equipe Kardume',
  SELF_ONBOARDING: 'A própria empresa',
  GUIDE: 'Curadoria do guia',
  IMPORT: 'Importação autorizada',
  USER_SUGGESTION: 'Sugestão de usuário',
};

const IdentityCard = styled.div`
  background: ${(p) => p.theme.surface};
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radiusLg};
  padding: 1.25rem;
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  margin-bottom: 1.25rem;

  .info {
    flex: 1;
    min-width: 0;
  }
  h2 {
    margin: 0;
    font-size: 1.2rem;
    font-weight: 800;
    color: ${(p) => p.theme.textPrimary};
  }
  .sub {
    margin: 0.1rem 0;
    font-size: 0.85rem;
    color: ${(p) => p.theme.textMuted};
  }
  .badges {
    display: flex;
    gap: 0.4rem;
    margin-top: 0.5rem;
    flex-wrap: wrap;
  }
`;

const IdRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.6rem;
  padding: 0.3rem 0.6rem;
  background: ${(p) => p.theme.surfaceAlt};
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radius};
  font-family: monospace;
  font-size: 0.72rem;
  color: ${(p) => p.theme.textSecondary};

  button {
    border: none;
    background: transparent;
    color: ${(p) => p.theme.primary};
    cursor: pointer;
    display: inline-flex;
  }
`;

const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.75rem;
  margin-bottom: 1.25rem;

  @media (max-width: 720px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const Stat = styled.div`
  background: ${(p) => p.theme.surface};
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radius};
  padding: 0.85rem 1rem;

  .label {
    font-size: 0.72rem;
    color: ${(p) => p.theme.textMuted};
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }
  .value {
    margin-top: 0.25rem;
    font-size: 1.05rem;
    font-weight: 700;
    color: ${(p) => p.theme.textPrimary};
  }
`;

const Tabs = styled.div`
  display: flex;
  gap: 1.25rem;
  border-bottom: 1px solid ${(p) => p.theme.border};
  margin-bottom: 1.25rem;
  overflow-x: auto;
`;

const Tab = styled.button<{ $active: boolean }>`
  border: none;
  background: transparent;
  padding: 0.6rem 0.1rem;
  font-size: 0.9rem;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  color: ${(p) => (p.$active ? p.theme.primary : p.theme.textMuted)};
  border-bottom: 2px solid ${(p) => (p.$active ? p.theme.primary : 'transparent')};
`;

const SectionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 1rem;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const Section = styled.div`
  background: ${(p) => p.theme.surface};
  border: 1px solid ${(p) => p.theme.border};
  border-radius: ${(p) => p.theme.radiusLg};
  padding: 1.1rem;
  margin-bottom: 1rem;

  h4 {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin: 0 0 0.85rem;
    font-size: 0.9rem;
    color: ${(p) => p.theme.textPrimary};
  }
  svg {
    color: ${(p) => p.theme.primary};
  }
`;

const Field = styled.div`
  margin-bottom: 0.7rem;

  .k {
    font-size: 0.68rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: ${(p) => p.theme.textMuted};
  }
  .v {
    font-size: 0.9rem;
    color: ${(p) => p.theme.textPrimary};
    word-break: break-word;
  }
`;

const FactGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.6rem 1rem;
`;

const CheckRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  padding: 0.6rem 0;
  border-bottom: 1px solid ${(p) => p.theme.border};

  &:last-child {
    border-bottom: none;
  }
  .body {
    flex: 1;
    min-width: 0;
  }
  .title {
    font-size: 0.88rem;
    font-weight: 600;
    color: ${(p) => p.theme.textPrimary};
  }
  .details {
    font-size: 0.78rem;
    color: ${(p) => p.theme.textMuted};
  }
`;

const Dot = styled.span<{ $tone: Tone }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-top: 0.35rem;
  flex-shrink: 0;
  background: ${(p) =>
    p.$tone === 'success' ? p.theme.success : p.$tone === 'danger' ? p.theme.error : p.theme.warning};
`;

const Muted = styled.span`
  color: ${(p) => p.theme.textMuted};
`;

const Placeholder = styled.p`
  color: ${(p) => p.theme.textMuted};
  font-size: 0.9rem;
  padding: 1.5rem 0;
`;

const DrawerActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Progress = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  margin-bottom: 0.5rem;

  .bar {
    height: 8px;
    border-radius: 999px;
    background: ${(p) => p.theme.border};
    overflow: hidden;
  }
  .bar > span {
    display: block;
    height: 100%;
    background: ${(p) => p.theme.primary};
  }
  .pct {
    font-size: 0.78rem;
    color: ${(p) => p.theme.textMuted};
  }
`;

const STEP_LABELS: Record<string, string> = {
  COMPANY_DATA: 'Dados da empresa',
  RESPONSIBLE: 'Responsável',
  CONTACT: 'Contato',
  ADDRESS: 'Endereço',
  BUSINESS_HOURS: 'Horários',
  PLAN: 'Plano',
};

const DAY_LABELS: Record<string, string> = {
  MONDAY: 'Segunda',
  TUESDAY: 'Terça',
  WEDNESDAY: 'Quarta',
  THURSDAY: 'Quinta',
  FRIDAY: 'Sexta',
  SATURDAY: 'Sábado',
  SUNDAY: 'Domingo',
};

/**
 * The core returns opening hours as a flat list where the same `dayOfWeek` may
 * repeat - one item per window, since a store can close for lunch. Grouping is
 * what turns that into one line per day (and keeps the React key unique).
 */
const groupBusinessHours = (
  hours: BusinessHourDto[]
): { dayOfWeek: string; label: string; text: string }[] => {
  const byDay: { dayOfWeek: string; label: string; windows: string[]; closed: boolean }[] = [];
  hours.forEach((hour) => {
    let day = byDay.find((it) => it.dayOfWeek === hour.dayOfWeek);
    if (!day) {
      day = {
        dayOfWeek: hour.dayOfWeek,
        label: DAY_LABELS[hour.dayOfWeek] ?? hour.dayOfWeek,
        windows: [],
        closed: hour.closed,
      };
      byDay.push(day);
    }
    if (hour.closed) return;
    day.closed = false;
    day.windows.push(`${hour.opensAt ?? '--'} às ${hour.closesAt ?? '--'}`);
  });
  return byDay.map(({ dayOfWeek, label, windows, closed }) => ({
    dayOfWeek,
    label,
    text: closed || windows.length === 0 ? 'Fechado' : windows.join(' · '),
  }));
};

type TabId = 'geral' | 'equipe' | 'financeiro' | 'disponibilidade' | 'cobrancas' | 'auditoria';

const TAB_ITEMS: { id: TabId; label: string }[] = [
  { id: 'geral', label: 'Geral' },
  { id: 'equipe', label: 'Equipe' },
  { id: 'financeiro', label: 'Financeiro' },
  { id: 'disponibilidade', label: 'Disponibilidade da loja' },
  { id: 'cobrancas', label: 'Cobranças' },
  { id: 'auditoria', label: 'Auditoria administrativa' },
];

const SUBSCRIPTION_STATUS_LABELS: Record<string, string> = {
  ACTIVE: 'Ativa',
  TRIALING: 'Em teste',
  PAST_DUE: 'Em atraso',
  CANCELED: 'Cancelada',
};

const INVOICE_STATUS_LABELS: Record<string, string> = {
  PAID: 'Paga',
  OPEN: 'Em aberto',
  OVERDUE: 'Vencida',
  CANCELED: 'Cancelada',
};

const CREDIT_STATUS_LABELS: Record<string, string> = {
  OPEN: 'Em aberto',
  APPLIED: 'Aplicado',
  CANCELED: 'Cancelado',
};

const BILLING_PERIOD_SUFFIX: Record<string, string> = {
  MONTHLY: '/mês',
  QUARTERLY: '/trimestre',
  YEARLY: '/ano',
};

const SUBSCRIPTION_ACTION_ERROR_MESSAGES: Record<string, string> = {
  SUBSCRIPTION_SAME_PLAN: 'A assinatura já está neste plano.',
  SUBSCRIPTION_CANCELED: 'Esta assinatura está cancelada - nenhuma das três ações se aplica.',
  GRACE_UNTIL_IN_THE_PAST: 'A data de carência precisa ser futura.',
  TRIAL_DAYS_INVALID: 'A duração do trial precisa ser positiva.',
};

/** O mesmo formulário serve os dois fluxos de plano: 'create' atribui o primeiro
 *  plano do lojista (ainda sem assinatura) e 'change' agenda a troca do vigente. */
type PlanFormMode = 'create' | 'change';

const BILLING_PERIOD_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: '(usar o período do plano)' },
  { value: 'MONTHLY', label: 'Mensal' },
  { value: 'QUARTERLY', label: 'Trimestral' },
  { value: 'YEARLY', label: 'Anual' },
];

function statusTone(status: MerchantStatus): Tone {
  switch (status) {
    case 'ACTIVE':
      return 'success';
    case 'PENDING':
      return 'warning';
    case 'SUSPENDED':
      return 'danger';
    default:
      return 'secondary';
  }
}

function subscriptionTone(status?: string | null): Tone {
  if (status === 'ACTIVE') return 'success';
  if (status === 'TRIALING' || status === 'PAST_DUE') return 'warning';
  if (status === 'CANCELED') return 'danger';
  return 'secondary';
}

function invoiceTone(status: string): Tone {
  if (status === 'PAID') return 'success';
  if (status === 'OPEN') return 'warning';
  return 'danger';
}

function creditTone(status: string): Tone {
  if (status === 'APPLIED') return 'success';
  if (status === 'OPEN') return 'warning';
  return 'secondary';
}

function employeeStatusTone(status: MerchantEmployeeStatus): Tone {
  if (status === 'ACTIVE') return 'success';
  if (status === 'PENDING_ACTIVATION') return 'warning';
  if (status === 'BLOCKED') return 'danger';
  return 'secondary';
}

const employeeRoleTone = (role: MerchantRole): Tone => (role === 'OWNER' ? 'danger' : 'info');

/**
 * Cadastro antigo pode não trazer `accessEnabled`; nesse caso o acesso segue o
 * status da conta - a mesma leitura que o painel do lojista faz.
 */
const isAccessAllowed = (employee: BackofficeMerchantEmployeeDto): boolean =>
  employee.accessEnabled ?? employee.status === 'ACTIVE';

function label(map: Record<string, string>, key?: string | null): string {
  if (!key) return '-';
  return map[key] ?? key;
}

interface MerchantDetailDrawerProps {
  merchant: MerchantDto | null;
  isOpen: boolean;
  onClose: () => void;
  onEdit: (merchant: MerchantDto) => void;
  onApprove?: (merchant: MerchantDto) => void;
  onReject?: (merchant: MerchantDto, reason: string) => void;
}

const MerchantDetailDrawer: React.FC<MerchantDetailDrawerProps> = ({ merchant, isOpen, onClose, onEdit, onApprove, onReject }) => {
  const [rejectOpen, setRejectOpen] = useState(false);
  const [tab, setTab] = useState<TabId>('geral');
  const [analysis, setAnalysis] = useState<MerchantBillingAnalysisDto | null>(null);
  const [availability, setAvailability] = useState<MerchantAvailabilityAnalysisDto | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [canManageSubscription, setCanManageSubscription] = useState(false);
  const [canManageMerchant, setCanManageMerchant] = useState(false);
  const [canManagePartnership, setCanManagePartnership] = useState(false);

  const merchantId = merchant?.id;

  // Permissões do operador logado (uma vez por sessão do drawer): decide se as
  // ações de plano/carência/trial (card #144) aparecem na aba Financeiro.
  useEffect(() => {
    let active = true;
    backofficeApi
      .getMe()
      .then(({ user }) => {
        if (!active) return;
        const isAdmin = user.role === 'ADMIN';
        setCanManageSubscription(isAdmin || user.effectivePermissions.includes('ROLE_BACKOFFICE_SUBSCRIPTION_MANAGE'));
        // A equipe da loja é parte do cadastro do lojista: quem pode editar o
        // lojista pode montar a equipe dele.
        setCanManageMerchant(isAdmin || user.effectivePermissions.includes('ROLE_BACKOFFICE_MERCHANT_MANAGE'));
        // Card 226 - permissão própria: sem ela os botões de parceria não
        // existem (e o endpoint responde 403 de qualquer forma).
        setCanManagePartnership(
          isAdmin || user.effectivePermissions.includes('ROLE_BACKOFFICE_PARTNERSHIP_MANAGE'),
        );
      })
      .catch(() => {
        /* sem permissão de leitura de sessão: mantém as ações escondidas */
      });
    return () => {
      active = false;
    };
  }, []);

  const loadAnalysis = React.useCallback(() => {
    if (!merchantId) return;
    setLoading(true);
    setError(null);
    return Promise.all([
      backofficeApi.getMerchantBillingAnalysis(merchantId),
      backofficeApi.getMerchantAvailabilityAnalysis(merchantId),
    ])
      .then(([billing, avail]) => {
        setAnalysis(billing);
        setAvailability(avail);
      })
      .catch(() => {
        setError('Não foi possível carregar os dados do lojista.');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [merchantId]);

  useEffect(() => {
    if (!isOpen || !merchantId) return;
    let active = true;
    setLoading(true);
    setError(null);
    Promise.all([
      backofficeApi.getMerchantBillingAnalysis(merchantId),
      backofficeApi.getMerchantAvailabilityAnalysis(merchantId),
    ])
      .then(([billing, avail]) => {
        if (!active) return;
        setAnalysis(billing);
        setAvailability(avail);
      })
      .catch(() => {
        if (!active) return;
        setError('Não foi possível carregar os dados do lojista.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [isOpen, merchantId]);

  /** Após trocar plano / conceder carência / ativar trial: recarrega a análise
   *  para a aba Financeiro e a Linha do tempo refletirem o novo estado, e o
   *  resumo no topo do drawer (status/plano) também atualiza. */
  const handleSubscriptionChanged = () => {
    loadAnalysis();
  };

  const copyId = () => {
    if (merchant?.id && navigator.clipboard) navigator.clipboard.writeText(merchant.id);
  };

  const planName = analysis?.merchant.planName ?? merchant?.planName ?? '-';
  const subscriptionStatusLabel = analysis?.merchant.subscriptionStatus
    ? label(SUBSCRIPTION_STATUS_LABELS, analysis.merchant.subscriptionStatus)
    : merchant?.billingStatus ?? '-';
  const totalPaid = analysis?.merchant.totalPaid ?? merchant?.totalPaid;
  const openCredits = analysis?.merchant.openCredits ?? merchant?.openCredits;

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      width="min(1000px, 92vw)"
      title={merchant ? `Lojista - ${merchant.tradeName || merchant.name}` : 'Lojista'}
      headerActions={
        merchant && (
          <DrawerActions>
            {onApprove && merchant.status !== 'ACTIVE' && (
              <Button
                variant="primary"
                disabled={!merchant.onboardingComplete}
                /* "Complete o onboarding" não dizia o que completar, e desde o
                   card 241 a empresa entra incompleta de propósito - o operador
                   chega aqui justamente para descobrir o que ainda falta. */
                title={
                  merchant.onboardingComplete
                    ? 'Aprovar e ativar'
                    : `Falta completar: ${(merchant.checklist ?? [])
                        .filter((c) => !c.complete)
                        .map((c) => STEP_LABELS[c.step] ?? c.step)
                        .join(', ')}`
                }
                onClick={() => onApprove(merchant)}
              >
                Aprovar
              </Button>
            )}
            {onReject && (
              <Button
                variant="ghost"
                onClick={() => setRejectOpen(true)}
              >
                Rejeitar
              </Button>
            )}
            <Button variant="outline" onClick={() => onEdit(merchant)}>
              <Edit3 size={15} /> Editar
            </Button>
          </DrawerActions>
        )
      }
    >
      {merchant && (
        <>
          <IdentityCard>
            <Avatar name={merchant.tradeName || merchant.name} src={merchant.logoUrl} size={64} />
            <div className="info">
              <h2>{merchant.tradeName || merchant.name}</h2>
              <p className="sub">{merchant.name}</p>
              <p className="sub">
                {formatDocument(merchant.document)}
                {merchant.ownerName ? ` · ${merchant.ownerName}` : ''}
              </p>
              <div className="badges">
                <Badge tone={statusTone(merchant.status)}>{MERCHANT_STATUS_LABELS[merchant.status]}</Badge>
                {analysis?.merchant.subscriptionStatus && (
                  <Badge tone={subscriptionTone(analysis.merchant.subscriptionStatus)}>
                    {label(SUBSCRIPTION_STATUS_LABELS, analysis.merchant.subscriptionStatus)}
                  </Badge>
                )}
              </div>
              <IdRow>
                ID: {merchant.id}
                <button type="button" onClick={copyId} aria-label="Copiar ID">
                  <Copy size={13} />
                </button>
              </IdRow>
            </div>
          </IdentityCard>

          <Stats>
            <Stat>
              <div className="label">Plano contratado</div>
              <div className="value">{planName}</div>
            </Stat>
            <Stat>
              <div className="label">Status da assinatura</div>
              <div className="value">{subscriptionStatusLabel}</div>
            </Stat>
            <Stat>
              <div className="label">Total pago</div>
              <div className="value">{formatCurrency(totalPaid)}</div>
            </Stat>
            <Stat>
              <div className="label">Créditos em aberto</div>
              <div className="value">{formatCurrency(openCredits)}</div>
            </Stat>
          </Stats>

          <Tabs>
            {TAB_ITEMS.map((t) => (
              <Tab key={t.id} $active={tab === t.id} onClick={() => setTab(t.id)}>
                {t.label}
              </Tab>
            ))}
          </Tabs>

          {tab === 'geral' && (
            <GeneralTab merchant={merchant} canManagePartnership={canManagePartnership} />
          )}
          {/* Equipe tem carregamento próprio (outros endpoints): o "Carregando…"
              e o erro abaixo são os da análise financeira e não valem para ela. */}
          {tab === 'equipe' && merchantId && <TeamTab merchantId={merchantId} canManage={canManageMerchant} />}
          {tab !== 'geral' && tab !== 'equipe' && loading && <Placeholder>Carregando…</Placeholder>}
          {tab !== 'geral' && tab !== 'equipe' && !loading && error && <Placeholder>{error}</Placeholder>}

          {tab === 'financeiro' && !loading && !error && merchantId && (
            <FinancialTab
              analysis={analysis}
              merchantId={merchantId}
              canManage={canManageSubscription}
              onChanged={handleSubscriptionChanged}
            />
          )}
          {tab === 'cobrancas' && !loading && !error && <BillingTab analysis={analysis} />}
          {tab === 'disponibilidade' && !loading && !error && <AvailabilityTab availability={availability} />}
          {tab === 'auditoria' && !loading && !error && <AuditTab analysis={analysis} />}
        </>
      )}

      {merchant && onReject && (
        <PromptModal
          isOpen={rejectOpen}
          danger
          title="Rejeitar lojista"
          message={
            <>
              Descreva o motivo da rejeição de <strong>{merchant.tradeName || merchant.name}</strong>. Essa
              justificativa fica registrada.
            </>
          }
          label="Motivo da rejeição"
          placeholder="Ex.: documentação incompleta ou inconsistente"
          confirmLabel="Rejeitar"
          onConfirm={(reason) => {
            onReject(merchant, reason);
            setRejectOpen(false);
          }}
          onClose={() => setRejectOpen(false)}
        />
      )}
    </Drawer>
  );
};

/* -------------------------------------------------------------------------- */

const GeneralTab: React.FC<{ merchant: MerchantDto; canManagePartnership: boolean }> = ({
  merchant,
  canManagePartnership,
}) => (
  <SectionGrid>
    {/* Card 226 - o bloco de parceria abre a aba: é o que o operador vem ver
        quando a pergunta é "esta loja tem selo?", e deixá-lo no fim faria a
        resposta depender de rolagem. */}
    <div>
      <MerchantPartnershipSection merchant={merchant} canManage={canManagePartnership} />
    </div>
    <Section>
      <h4>
        <ClipboardCheck size={16} /> Onboarding
      </h4>
      <Progress>
        <div className="bar">
          <span style={{ width: `${merchant.onboardingProgress ?? 0}%` }} />
        </div>
        <div className="pct">
          {merchant.onboardingProgress ?? 0}% concluído ·{' '}
          {merchant.canCreateAds ? 'Apto a criar anúncios' : 'Ainda não apto a anunciar'}
        </div>
      </Progress>
      {(merchant.checklist ?? []).map((c) => (
        <Field key={c.step}>
          <div className="k">{STEP_LABELS[c.step] ?? c.step}</div>
          <div className="v">
            <Badge tone={c.complete ? 'success' : 'warning'}>{c.complete ? 'Completo' : 'Pendente'}</Badge>
          </div>
        </Field>
      ))}
      {merchant.rejectionReason && <Muted>Rejeição: {merchant.rejectionReason}</Muted>}
    </Section>

    <Section>
      <h4>
        <Building2 size={16} /> Identificação
      </h4>
      <Field>
        <div className="k">Nome fantasia</div>
        <div className="v">{merchant.tradeName || '-'}</div>
      </Field>
      <Field>
        <div className="k">Razão social</div>
        <div className="v">{merchant.name}</div>
      </Field>
      <Field>
        <div className="k">Documento</div>
        <div className="v">{formatDocument(merchant.document)}</div>
      </Field>
      <Field>
        <div className="k">Categoria</div>
        <div className="v">{merchant.categoryName ?? '-'}</div>
      </Field>
      {/* Card 231, §38.3 - a procedência fica ao lado da identificação porque é
          isso que ela é: parte de quem esta empresa é no cadastro. Nula para
          quem já estava aqui antes da coluna existir, e "-" é a resposta
          honesta nesse caso - melhor do que um "Backoffice" chutado que o funil
          comercial leria como origem real. */}
      <Field>
        <div className="k">Origem</div>
        <div className="v">
          {merchant.source ? MERCHANT_SOURCE_LABELS[merchant.source] : '-'}
          {merchant.sourceReference ? ` · ${merchant.sourceReference}` : ''}
        </div>
      </Field>
      {merchant.sourceCollectedAt && (
        <Field>
          <div className="k">Dado coletado em</div>
          <div className="v">{formatDate(merchant.sourceCollectedAt)}</div>
        </Field>
      )}
    </Section>

    <Section>
      <h4>
        <User size={16} /> Responsável
      </h4>
      <Field>
        <div className="k">Nome</div>
        <div className="v">{merchant.responsible?.name ?? merchant.ownerName ?? '-'}</div>
      </Field>
      {merchant.responsible?.role && (
        <Field>
          <div className="k">Cargo</div>
          <div className="v">{merchant.responsible.role}</div>
        </Field>
      )}
      <Field>
        <div className="k">E-mail</div>
        <div className="v">{merchant.responsible?.email ?? merchant.email ?? '-'}</div>
      </Field>
      <Field>
        <div className="k">Telefone</div>
        <div className="v">{formatPhone(merchant.responsible?.phone ?? merchant.phone) || '-'}</div>
      </Field>
    </Section>

    <Section>
      <h4>
        <Phone size={16} /> Contato da loja
      </h4>
      <Field>
        <div className="k">E-mail</div>
        <div className="v">{merchant.email}</div>
      </Field>
      <Field>
        <div className="k">Telefone</div>
        <div className="v">{formatPhone(merchant.phone) || '-'}</div>
      </Field>
      {merchant.whatsapp && (
        <Field>
          <div className="k">WhatsApp</div>
          <div className="v">{formatPhone(merchant.whatsapp)}</div>
        </Field>
      )}
      {merchant.website && (
        <Field>
          <div className="k">Site</div>
          <div className="v">{merchant.website}</div>
        </Field>
      )}
      {merchant.instagram && (
        <Field>
          <div className="k">Instagram</div>
          <div className="v">{merchant.instagram}</div>
        </Field>
      )}
    </Section>

    <Section>
      <h4>
        <Link2 size={16} /> Canais de venda
      </h4>
      {(merchant.externalLinks ?? []).length > 0 ? (
        (merchant.externalLinks ?? []).map((link, index) => (
          // OTHER may repeat, so the platform alone is not a stable key here.
          <Field key={`${link.platform}-${index}`}>
            <div className="k">{link.label}</div>
            <div className="v">{link.url}</div>
          </Field>
        ))
      ) : (
        <Placeholder>Nenhum canal cadastrado.</Placeholder>
      )}
    </Section>

    <Section>
      <h4>
        <MapPin size={16} /> Endereço
      </h4>
      {merchant.address ? (
        <>
          <Field>
            <div className="k">Logradouro</div>
            <div className="v">
              {merchant.address.street}, {merchant.address.number}
            </div>
          </Field>
          <Field>
            <div className="k">Bairro</div>
            <div className="v">{merchant.address.district}</div>
          </Field>
          <Field>
            <div className="k">Cidade/UF</div>
            <div className="v">
              {merchant.address.city}/{merchant.address.state}
            </div>
          </Field>
          <Field>
            <div className="k">CEP</div>
            <div className="v">{merchant.address.zipCode}</div>
          </Field>
        </>
      ) : (
        <Placeholder>Endereço não informado.</Placeholder>
      )}
    </Section>

    <Section>
      <h4>
        <Clock3 size={16} /> Horários
      </h4>
      {(merchant.businessHours ?? []).length > 0 ? (
        groupBusinessHours(merchant.businessHours ?? []).map((day) => (
          <Field key={day.dayOfWeek}>
            <div className="k">{day.label}</div>
            <div className="v">{day.text}</div>
          </Field>
        ))
      ) : (
        <Placeholder>Horários não informados.</Placeholder>
      )}
    </Section>

    <Section>
      <h4>
        <Activity size={16} /> Operação
      </h4>
      <Field>
        <div className="k">Status</div>
        <div className="v">
          <Badge tone={statusTone(merchant.status)}>{MERCHANT_STATUS_LABELS[merchant.status]}</Badge>
        </div>
      </Field>
      <Field>
        <div className="k">Onboarding</div>
        <div className="v">{merchant.onboardingStage ?? '-'}</div>
      </Field>
      <Field>
        <div className="k">Criado em</div>
        <div className="v">{formatDate(merchant.createdAt)}</div>
      </Field>
      <Field>
        <div className="k">Atualizado em</div>
        <div className="v">{formatDate(merchant.updatedAt)}</div>
      </Field>
    </Section>

    <Section>
      <h4>
        <Boxes size={16} /> Catálogo
      </h4>
      <Field>
        <div className="k">Produtos</div>
        <div className="v">{merchant.productsCount ?? '-'}</div>
      </Field>
      <Field>
        <div className="k">Categorias</div>
        <div className="v">{merchant.categoriesCount ?? '-'}</div>
      </Field>
      <Field>
        <div className="k">Complementos</div>
        <div className="v">{merchant.complementsCount ?? '-'}</div>
      </Field>
    </Section>
  </SectionGrid>
);

const ActionsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  margin-top: 1.1rem;
`;

const FormGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
`;

const Hint = styled.p`
  font-size: 0.82rem;
  color: ${(p) => p.theme.textMuted};
  margin: 0.4rem 0 0;
  line-height: 1.5;
`;

interface FinancialTabProps {
  analysis: MerchantBillingAnalysisDto | null;
  merchantId: string;
  canManage: boolean;
  onChanged: () => void;
}

const FinancialTab: React.FC<FinancialTabProps> = ({ analysis, merchantId, canManage, onChanged }) => {
  const current: SubscriptionDto | null | undefined = analysis?.currentSubscription;
  const subscriptions = analysis?.subscriptions ?? [];
  const { showNotification } = useNotification();

  // --- Atribuir / trocar plano ---------------------------------------------
  // Atribuir o primeiro plano e trocar de plano pedem exatamente os mesmos dados,
  // então compartilham estado e formulário; só o envio e um campo extra mudam.
  const [planFormMode, setPlanFormMode] = useState<PlanFormMode | null>(null);
  const [plans, setPlans] = useState<PlanDto[] | null>(null);
  const [plansLoading, setPlansLoading] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [priceOverride, setPriceOverride] = useState<number | undefined>(undefined);
  const [periodOverride, setPeriodOverride] = useState('');
  const [changePlanReason, setChangePlanReason] = useState('');
  const [initialTrialDays, setInitialTrialDays] = useState('');
  const [submittingPlan, setSubmittingPlan] = useState(false);

  const isAssigningPlan = planFormMode === 'create';
  const parsedTrialDays = initialTrialDays.trim() ? Number(initialTrialDays) : undefined;
  const trialDaysInvalid =
    parsedTrialDays !== undefined && (!Number.isFinite(parsedTrialDays) || parsedTrialDays <= 0);

  const openPlanForm = async (mode: PlanFormMode) => {
    setSelectedPlanId('');
    setPriceOverride(undefined);
    setPeriodOverride('');
    setChangePlanReason('');
    setInitialTrialDays('');
    setPlanFormMode(mode);
    if (!plans) {
      setPlansLoading(true);
      try {
        const page = await backofficeApi.listPlans({ size: 200 });
        setPlans(page.content.filter((p) => p.status === 'ACTIVE'));
      } catch (err) {
        showNotification(extractErrorMessage(err, 'Não foi possível carregar os planos.'), 'error');
        setPlanFormMode(null);
      } finally {
        setPlansLoading(false);
      }
    }
  };

  const confirmAssignPlan = async () => {
    if (!selectedPlanId || trialDaysInvalid || submittingPlan) return;
    setSubmittingPlan(true);
    try {
      await backofficeApi.createSubscription({
        merchantId,
        planId: selectedPlanId,
        price: priceOverride,
        billingPeriod: (periodOverride || undefined) as BillingPeriod | undefined,
        trialDays: parsedTrialDays,
      });
      setPlanFormMode(null);
      showNotification('Plano atribuído ao lojista.', 'success');
      onChanged();
    } catch (err) {
      showNotification(
        extractErrorMessage(err, 'Não foi possível atribuir o plano.', SUBSCRIPTION_ACTION_ERROR_MESSAGES),
        'error'
      );
    } finally {
      setSubmittingPlan(false);
    }
  };

  const confirmChangePlan = async () => {
    if (!current || !selectedPlanId || submittingPlan) return;
    setSubmittingPlan(true);
    try {
      await backofficeApi.changeSubscriptionPlan(current.id, {
        planId: selectedPlanId,
        price: priceOverride,
        billingPeriod: (periodOverride || undefined) as BillingPeriod | undefined,
        reason: changePlanReason.trim() || undefined,
      });
      setPlanFormMode(null);
      showNotification('Plano trocado. Vale a partir do próximo ciclo.', 'success');
      onChanged();
    } catch (err) {
      showNotification(
        extractErrorMessage(err, 'Não foi possível trocar o plano.', SUBSCRIPTION_ACTION_ERROR_MESSAGES),
        'error'
      );
    } finally {
      setSubmittingPlan(false);
    }
  };

  // --- Conceder carência ----------------------------------------------------
  const [graceModalOpen, setGraceModalOpen] = useState(false);
  const [graceUntil, setGraceUntil] = useState('');
  const [graceReason, setGraceReason] = useState('');
  const [grantingGrace, setGrantingGrace] = useState(false);

  const openGraceModal = () => {
    setGraceUntil('');
    setGraceReason('');
    setGraceModalOpen(true);
  };

  const confirmGracePeriod = async () => {
    if (!current || !graceUntil) return;
    setGrantingGrace(true);
    try {
      await backofficeApi.grantGracePeriod(current.id, {
        graceUntil: new Date(`${graceUntil}T23:59:59`).toISOString(),
        reason: graceReason.trim() || undefined,
      });
      setGraceModalOpen(false);
      showNotification('Carência concedida.', 'success');
      onChanged();
    } catch (err) {
      showNotification(
        extractErrorMessage(err, 'Não foi possível conceder a carência.', SUBSCRIPTION_ACTION_ERROR_MESSAGES),
        'error'
      );
    } finally {
      setGrantingGrace(false);
    }
  };

  // --- Ativar trial -----------------------------------------------------
  const [trialModalOpen, setTrialModalOpen] = useState(false);
  const [trialDays, setTrialDays] = useState('');
  const [trialReason, setTrialReason] = useState('');
  const [startingTrial, setStartingTrial] = useState(false);

  const openTrialModal = () => {
    setTrialDays('');
    setTrialReason('');
    setTrialModalOpen(true);
  };

  const confirmStartTrial = async () => {
    if (!current) return;
    const days = Number(trialDays);
    if (!Number.isFinite(days) || days <= 0) return;
    setStartingTrial(true);
    try {
      await backofficeApi.startTrial(current.id, {
        trialDays: days,
        reason: trialReason.trim() || undefined,
      });
      setTrialModalOpen(false);
      showNotification('Trial ativado.', 'success');
      onChanged();
    } catch (err) {
      showNotification(
        extractErrorMessage(err, 'Não foi possível ativar o trial.', SUBSCRIPTION_ACTION_ERROR_MESSAGES),
        'error'
      );
    } finally {
      setStartingTrial(false);
    }
  };

  // Assinatura cancelada não aceita mais nenhuma ação; sem assinatura, a única
  // ação disponível é atribuir o primeiro plano.
  const canAct = canManage && (!current || current.status !== 'CANCELED');

  return (
    <>
      {current ? (
        <Section>
          <h4>
            <CreditCard size={16} /> Assinatura atual
          </h4>
          <FactGrid>
            <Field>
              <div className="k">Plano</div>
              <div className="v">{current.planName ?? '-'}</div>
              {current.scheduledPrice != null && (
                <Muted>
                  A partir do próximo ciclo: {formatCurrency(current.scheduledPrice)}
                  {current.scheduledBillingPeriod ? BILLING_PERIOD_SUFFIX[current.scheduledBillingPeriod] ?? '' : ''}
                </Muted>
              )}
            </Field>
            <Field>
              <div className="k">Status</div>
              <div className="v">
                <Badge tone={subscriptionTone(current.status)}>{label(SUBSCRIPTION_STATUS_LABELS, current.status)}</Badge>
              </div>
              {current.graceUntil && <Muted>Carência até {formatDate(current.graceUntil)}</Muted>}
            </Field>
            <Field>
              <div className="k">Valor</div>
              <div className="v">
                {formatCurrency(current.price)}
                <Muted>{BILLING_PERIOD_SUFFIX[current.billingPeriod] ?? ''}</Muted>
              </div>
            </Field>
            <Field>
              <div className="k">Período atual</div>
              <div className="v">
                {formatDate(current.currentPeriodStart)} - {formatDate(current.currentPeriodEnd)}
              </div>
            </Field>
            <Field>
              <div className="k">Próxima fatura</div>
              <div className="v">{current.nextInvoiceAt ? formatDate(current.nextInvoiceAt) : '-'}</div>
            </Field>
          </FactGrid>

          {canAct && (
            <ActionsRow>
              <Button variant="outline" onClick={() => openPlanForm('change')}>
                Trocar de plano
              </Button>
              <Button variant="outline" onClick={openGraceModal}>
                Conceder carência
              </Button>
              <Button variant="outline" onClick={openTrialModal}>
                Ativar trial
              </Button>
            </ActionsRow>
          )}
        </Section>
      ) : (
        <Section>
          <h4>
            <CreditCard size={16} /> Assinatura
          </h4>
          <Placeholder>Este lojista ainda não possui assinatura.</Placeholder>
          {canAct && (
            <ActionsRow>
              <Button variant="primary" onClick={() => openPlanForm('create')}>
                Atribuir plano
              </Button>
            </ActionsRow>
          )}
        </Section>
      )}

      <Section>
        <h4>
          <CalendarClock size={16} /> Linha do tempo da assinatura
        </h4>
        {subscriptions.length === 0 ? (
          <Placeholder>Sem histórico de assinaturas.</Placeholder>
        ) : (
          <TableContainer>
            <Table>
              <thead>
                <tr>
                  <th>Criada em</th>
                  <th>Plano</th>
                  <th>Valor</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {subscriptions.map((s) => (
                  <tr key={s.id}>
                    <td>{formatDate(s.createdAt)}</td>
                    <td>{s.planName ?? '-'}</td>
                    <td>{formatCurrency(s.price)}</td>
                    <td>
                      <Badge tone={subscriptionTone(s.status)}>{label(SUBSCRIPTION_STATUS_LABELS, s.status)}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableContainer>
        )}
      </Section>

      {/* Atribuir o primeiro plano / trocar de plano */}
      <Modal
        isOpen={planFormMode !== null}
        onClose={() => !submittingPlan && setPlanFormMode(null)}
        title={isAssigningPlan ? 'Atribuir plano' : 'Trocar de plano'}
        footer={
          <>
            <Button variant="ghost" onClick={() => setPlanFormMode(null)} disabled={submittingPlan}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={isAssigningPlan ? confirmAssignPlan : confirmChangePlan}
              isLoading={submittingPlan}
              disabled={!selectedPlanId || (isAssigningPlan && trialDaysInvalid)}
            >
              {isAssigningPlan ? 'Atribuir plano' : 'Confirmar troca'}
            </Button>
          </>
        }
      >
        <FormGrid>
          <Select
            label={isAssigningPlan ? 'Plano' : 'Novo plano'}
            value={selectedPlanId}
            onChange={(e) => setSelectedPlanId(e.target.value)}
            disabled={plansLoading}
            options={[
              { value: '', label: plansLoading ? 'Carregando…' : 'Selecione um plano' },
              ...(plans ?? [])
                .filter((p) => p.id !== current?.planId)
                .map((p) => ({ value: p.id, label: `${p.name} - ${formatCurrency(p.price)}${BILLING_PERIOD_SUFFIX[p.billingPeriod] ?? ''}` })),
            ]}
          />
          <CurrencyInput
            label="Preço negociado (opcional)"
            value={priceOverride}
            onChange={setPriceOverride}
            placeholder="Deixe em branco para usar o preço do plano"
          />
          <Select
            label="Período de cobrança (opcional)"
            value={periodOverride}
            onChange={(e) => setPeriodOverride(e.target.value)}
            options={BILLING_PERIOD_OPTIONS}
          />
          {isAssigningPlan ? (
            <TextInput
              type="number"
              min={1}
              label="Dias de trial (opcional)"
              value={initialTrialDays}
              onChange={(e) => setInitialTrialDays(e.target.value)}
              placeholder="Deixe em branco para já cobrar o plano"
            />
          ) : (
            <TextInput
              label="Motivo (opcional, vai para a auditoria)"
              value={changePlanReason}
              onChange={(e) => setChangePlanReason(e.target.value)}
              placeholder="Ex.: negociação comercial com o lojista"
            />
          )}
        </FormGrid>
        <Hint>
          {isAssigningPlan
            ? 'Cria a assinatura já no plano escolhido e inicia o ciclo de cobrança. Com dias de trial, a primeira fatura só vence depois da cortesia.'
            : 'Vale a partir do próximo ciclo - o período corrente já foi cobrado. Em downgrade, a diferença não usada vira crédito proporcional.'}
        </Hint>
      </Modal>

      {/* Conceder carência */}
      <Modal
        isOpen={graceModalOpen}
        onClose={() => !grantingGrace && setGraceModalOpen(false)}
        title="Conceder carência"
        footer={
          <>
            <Button variant="ghost" onClick={() => setGraceModalOpen(false)} disabled={grantingGrace}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={confirmGracePeriod} isLoading={grantingGrace} disabled={!graceUntil}>
              Conceder carência
            </Button>
          </>
        }
      >
        <FormGrid>
          <TextInput
            type="date"
            label="Carência até"
            value={graceUntil}
            onChange={(e) => setGraceUntil(e.target.value)}
            min={new Date().toISOString().slice(0, 10)}
          />
          <TextInput
            label="Motivo (opcional, vai para a auditoria)"
            value={graceReason}
            onChange={(e) => setGraceReason(e.target.value)}
            placeholder="Ex.: negociado até 30/09 por telefone"
          />
        </FormGrid>
        <Hint>
          É uma data, não um prazo: o valor negociado é o que fica registrado. Se o lojista estiver em
          atraso (PAST_DUE), ele volta para ACTIVE.
        </Hint>
      </Modal>

      {/* Ativar trial */}
      <Modal
        isOpen={trialModalOpen}
        onClose={() => !startingTrial && setTrialModalOpen(false)}
        title="Ativar trial"
        footer={
          <>
            <Button variant="ghost" onClick={() => setTrialModalOpen(false)} disabled={startingTrial}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={confirmStartTrial}
              isLoading={startingTrial}
              disabled={!trialDays || Number(trialDays) <= 0}
            >
              Ativar trial
            </Button>
          </>
        }
      >
        <FormGrid>
          <TextInput
            type="number"
            min={1}
            label="Dias de trial"
            value={trialDays}
            onChange={(e) => setTrialDays(e.target.value)}
            placeholder="Ex.: 15"
          />
          <TextInput
            label="Motivo (opcional, vai para a auditoria)"
            value={trialReason}
            onChange={(e) => setTrialReason(e.target.value)}
            placeholder="Ex.: cortesia comercial"
          />
        </FormGrid>
        <Hint>Estende o ciclo: a próxima fatura passa para depois dos dias de trial concedidos.</Hint>
      </Modal>
    </>
  );
};

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-bottom: 0.85rem;

  h4 {
    margin: 0;
  }
`;

const Person = styled.div`
  display: flex;
  align-items: center;
  gap: 0.7rem;

  strong {
    display: block;
    color: ${(p) => p.theme.textPrimary};
  }
  .email {
    font-size: 0.8rem;
    color: ${(p) => p.theme.textMuted};
  }
`;

const RowActions = styled.div`
  display: flex;
  gap: 0.4rem;
  justify-content: flex-end;
`;

/**
 * Equipe da loja vista pelo backoffice.
 *
 * Existe para a operação não ficar refém do lojista entrar no painel dele para
 * montar a equipe - que é justamente o passo que trava o começo do uso. Cada
 * ação grava sozinha, pelos endpoints dedicados: não há "salvar tudo" aqui,
 * porque criar funcionário dispara e-mail com senha temporária e isso não tem
 * como voltar atrás num "Cancelar" de formulário.
 */
const TeamTab: React.FC<{ merchantId: string; canManage: boolean }> = ({ merchantId, canManage }) => {
  const { showNotification } = useNotification();
  const [employees, setEmployees] = useState<BackofficeMerchantEmployeeDto[]>([]);
  const [profiles, setProfiles] = useState<BackofficeMerchantAccessProfileOptionDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<MerchantEmployeeFormValue>(emptyMerchantEmployee());
  const [saving, setSaving] = useState(false);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  /**
   * Funcionário aguardando confirmação do reenvio de credenciais.
   *
   * Confirma porque a ação não é reversível nem repetível de graça: cada envio
   * gera uma senha nova, invalida a anterior e derruba quem estiver logado. Um
   * clique errado tira do ar alguém que estava trabalhando, e o e-mail que já
   * tinha chegado deixa de valer.
   */
  const [resetting, setResetting] = useState<BackofficeMerchantEmployeeDto | null>(null);
  const [resetLoading, setResetLoading] = useState(false);

  const load = React.useCallback(() => {
    setLoading(true);
    setError(null);
    return Promise.all([
      backofficeApi.listMerchantEmployees(merchantId),
      // Perfis vêm da própria loja e costumam vir vazios: quem os cria é o
      // lojista, no painel dele. A lista vazia não é erro - é o estado normal
      // de uma loja recém-cadastrada, e o formulário explica isso na tela.
      backofficeApi.listMerchantAccessProfiles(merchantId),
    ])
      .then(([team, storeProfiles]) => {
        setEmployees(team);
        setProfiles(storeProfiles);
      })
      .catch((err) => {
        setError(extractErrorMessage(err, 'Não foi possível carregar a equipe do lojista.'));
      })
      .finally(() => {
        setLoading(false);
      });
  }, [merchantId]);

  useEffect(() => {
    load();
  }, [load]);

  /** E-mails já ocupados NESTA loja. A unicidade é global e quem a garante é o
   *  core - isto só evita o request no caso que dá para ver daqui. */
  const takenEmails = useMemo(
    () => employees.filter((it) => it.id !== editingId).map((it) => it.email),
    [employees, editingId]
  );
  const errors = useMemo(
    () => validateMerchantEmployee(form, { takenEmails }),
    [form, takenEmails]
  );

  const openCreate = () => {
    setEditingId(null);
    // Sem perfil na loja, o único cargo possível é o de proprietário.
    setForm(emptyMerchantEmployee(profiles.length > 0 ? 'STAFF' : 'OWNER'));
    setFormOpen(true);
  };

  const openEdit = (employee: BackofficeMerchantEmployeeDto) => {
    setEditingId(employee.id);
    setForm(merchantEmployeeFromDto(employee));
    setFormOpen(true);
  };

  const submit = async () => {
    if (saving || isBlankMerchantEmployee(form) || hasMerchantEmployeeErrors(errors)) return;
    setSaving(true);
    try {
      const payload = merchantEmployeeToPayload(form);
      if (editingId) {
        await backofficeApi.updateMerchantEmployee(merchantId, editingId, payload);
        showNotification('Funcionário atualizado.', 'success');
      } else {
        await backofficeApi.createMerchantEmployee(merchantId, payload);
        showNotification('Funcionário criado. A senha temporária foi enviada por e-mail.', 'success');
      }
      setFormOpen(false);
      await load();
    } catch (err) {
      showNotification(
        extractErrorMessage(err, 'Não foi possível salvar o funcionário.', MERCHANT_EMPLOYEE_ERROR_MESSAGES),
        'error'
      );
    } finally {
      setSaving(false);
    }
  };

  const toggleAccess = async (employee: BackofficeMerchantEmployeeDto) => {
    const allowed = isAccessAllowed(employee);
    setTogglingId(employee.id);
    try {
      // Status e acesso andam juntos: bloquear só o acesso deixaria a conta
      // "Ativa" na listagem sem conseguir entrar - o painel do lojista faz igual.
      await backofficeApi.setMerchantEmployeeStatus(merchantId, employee.id, {
        status: allowed ? 'BLOCKED' : 'ACTIVE',
        accessEnabled: !allowed,
      });
      showNotification(allowed ? 'Acesso bloqueado.' : 'Acesso liberado.', 'success');
      await load();
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível alterar o acesso.'), 'error');
    } finally {
      setTogglingId(null);
    }
  };

  /**
   * Reenvia as credenciais: senha temporária nova por e-mail, troca obrigatória
   * no primeiro acesso.
   *
   * É o caminho para o caso mais comum do suporte - a pessoa não recebeu o
   * e-mail de cadastro, ou perdeu o acesso. O painel do lojista tem o "esqueci
   * minha senha", mas ele exige exatamente o que está faltando: alguém que
   * consiga chegar até aquela tela.
   */
  const confirmResetCredentials = async () => {
    if (!resetting) return;
    setResetLoading(true);
    try {
      await backofficeApi.resetMerchantEmployeeCredentials(merchantId, resetting.id);
      // "disparado", e não "entregue": o envio é assíncrono e acontece depois do
      // commit, então esta resposta não pode afirmar que o e-mail chegou. O que
      // ela garante é que a senha foi trocada e o e-mail saiu da fila - e é isso
      // que o texto diz. Uma falha de SMTP a partir daqui vira ERROR no log do
      // servidor, e não aparece nesta tela.
      showNotification(
        `Senha temporária redefinida e e-mail disparado para ${resetting.email}. ` +
          'A troca é obrigatória no primeiro acesso - confirme com a pessoa se a mensagem chegou.',
        'success'
      );
      setResetting(null);
      // Recarrega porque as sessões daquele funcionário caíram: a linha pode
      // ter mudado de estado no servidor, e mostrar o valor velho aqui faria o
      // operador achar que nada aconteceu.
      await load();
    } catch (err) {
      showNotification(
        extractErrorMessage(
          err,
          'Não foi possível reenviar as credenciais.',
          MERCHANT_EMPLOYEE_ERROR_MESSAGES
        ),
        'error'
      );
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <>
      <Section>
        <SectionHeader>
          <h4>
            <Users size={16} /> Equipe da loja
          </h4>
          {canManage && (
            <Button variant="primary" onClick={openCreate} disabled={loading}>
              <Plus size={16} /> Novo funcionário
            </Button>
          )}
        </SectionHeader>

        {loading && <Placeholder>Carregando equipe…</Placeholder>}
        {!loading && error && <Placeholder>{error}</Placeholder>}
        {!loading && !error && employees.length === 0 && (
          <Placeholder>
            Nenhum funcionário cadastrado - sem equipe, ninguém consegue entrar no painel desta loja.
          </Placeholder>
        )}

        {!loading && !error && employees.length > 0 && (
          <TableContainer>
            <Table>
              <thead>
                <tr>
                  <th>Funcionário</th>
                  <th>Cargo</th>
                  <th>Status</th>
                  <th>Acesso</th>
                  <th>Perfil de acesso</th>
                  <th style={{ textAlign: 'right' }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {employees.map((employee) => {
                  const allowed = isAccessAllowed(employee);
                  return (
                    <tr key={employee.id}>
                      <td>
                        <Person>
                          <Avatar name={`${employee.firstName} ${employee.lastName}`} size={32} />
                          <div>
                            <strong>
                              {employee.firstName} {employee.lastName}
                            </strong>
                            <div className="email">{employee.email}</div>
                          </div>
                        </Person>
                      </td>
                      <td>
                        <Badge tone={employeeRoleTone(employee.role)}>{MERCHANT_ROLE_LABELS[employee.role]}</Badge>
                      </td>
                      <td>
                        <Badge tone={employeeStatusTone(employee.status)}>
                          {label(MERCHANT_EMPLOYEE_STATUS_LABELS, employee.status)}
                        </Badge>
                      </td>
                      <td>
                        <Badge tone={allowed ? 'success' : 'danger'}>{allowed ? 'Liberado' : 'Bloqueado'}</Badge>
                      </td>
                      <td>
                        {employee.role === 'OWNER' ? (
                          <Badge tone="danger">Acesso total</Badge>
                        ) : employee.accessProfileName ? (
                          <Badge tone="info">{employee.accessProfileName}</Badge>
                        ) : (
                          <Badge tone="secondary">Sem perfil</Badge>
                        )}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {canManage ? (
                          <RowActions>
                            <ActionButton title="Editar" onClick={() => openEdit(employee)}>
                              <Edit3 size={16} />
                            </ActionButton>
                            {/* Vale para o proprietário também - e principalmente
                                para ele: é o único que consegue entrar numa loja
                                que ainda não tem perfil de acesso, então é o
                                cadastro que trava o começo do uso quando o e-mail
                                não chega. Desabilitado com acesso bloqueado: o
                                login recusaria a senha nova de qualquer jeito, e
                                o e-mail só confundiria. */}
                            <ActionButton
                              title={
                                allowed
                                  ? 'Reenviar credenciais por e-mail'
                                  : 'Libere o acesso para poder reenviar as credenciais'
                              }
                              disabled={!allowed || resetLoading}
                              onClick={() => setResetting(employee)}
                            >
                              <KeyRound size={16} />
                            </ActionButton>
                            {/* Proprietário fica de fora: bloquear o dono pela ficha
                                da loja deixaria a loja sem ninguém para entrar no
                                painel. Suspender a loja inteira é outra ação, no topo. */}
                            {employee.role !== 'OWNER' && (
                              <ActionButton
                                title={allowed ? 'Bloquear acesso' : 'Liberar acesso'}
                                disabled={togglingId === employee.id}
                                onClick={() => toggleAccess(employee)}
                              >
                                {allowed ? <Ban size={16} /> : <CheckCircle2 size={16} />}
                              </ActionButton>
                            )}
                          </RowActions>
                        ) : (
                          <Muted>-</Muted>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </TableContainer>
        )}
      </Section>

      <Modal
        isOpen={formOpen}
        onClose={() => !saving && setFormOpen(false)}
        width="640px"
        title={editingId ? 'Editar funcionário' : 'Novo funcionário'}
        footer={
          <>
            <Button variant="ghost" onClick={() => setFormOpen(false)} disabled={saving}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={submit}
              isLoading={saving}
              disabled={isBlankMerchantEmployee(form) || hasMerchantEmployeeErrors(errors)}
            >
              {editingId ? 'Salvar alterações' : 'Criar funcionário'}
            </Button>
          </>
        }
      >
        <MerchantEmployeeFields
          value={form}
          onChange={setForm}
          profiles={profiles}
          errors={errors}
          disabled={saving}
          noProfilesHint={
            <>
              Esta loja ainda não tem perfil de acesso - quem os cria é o próprio lojista, no painel
              dele. Enquanto não houver nenhum, só dá para cadastrar o <strong>proprietário</strong>,
              que é super-usuário da loja e não depende de perfil.
            </>
          }
        />
      </Modal>

      <ConfirmModal
        isOpen={!!resetting}
        title="Reenviar credenciais"
        message={
          <>
            Enviar uma <strong>senha temporária nova</strong> para{' '}
            <strong>{resetting?.email}</strong>?
            <br />
            <br />
            A senha atual deixa de funcionar e as sessões abertas deste funcionário caem na hora. A
            troca de senha é obrigatória no primeiro acesso.
          </>
        }
        confirmLabel="Enviar credenciais"
        loading={resetLoading}
        onConfirm={confirmResetCredentials}
        onClose={() => !resetLoading && setResetting(null)}
      />
    </>
  );
};

const BillingTab: React.FC<{ analysis: MerchantBillingAnalysisDto | null }> = ({ analysis }) => {
  const invoices: InvoiceDto[] = analysis?.invoices ?? [];
  const credits: SubscriptionCreditDto[] = analysis?.credits ?? [];

  if (invoices.length === 0 && credits.length === 0) {
    return <Placeholder>Nenhuma cobrança registrada para este lojista.</Placeholder>;
  }

  return (
    <>
      <Section>
        <h4>
          <CreditCard size={16} /> Faturas
        </h4>
        {invoices.length === 0 ? (
          <Placeholder>Sem faturas.</Placeholder>
        ) : (
          <TableContainer>
            <Table>
              <thead>
                <tr>
                  <th>Vencimento</th>
                  <th>Plano</th>
                  <th>Valor</th>
                  <th>Status</th>
                  <th>Pagamento</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((invoice) => (
                  <tr key={invoice.id}>
                    <td>{formatDate(invoice.dueDate)}</td>
                    <td>{invoice.planName}</td>
                    <td>{formatCurrency(invoice.amount)}</td>
                    <td>
                      <Badge tone={invoiceTone(invoice.status)}>{label(INVOICE_STATUS_LABELS, invoice.status)}</Badge>
                    </td>
                    <td>{invoice.paidAt ? formatDate(invoice.paidAt) : <Muted>-</Muted>}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableContainer>
        )}
      </Section>

      {credits.length > 0 && (
        <Section>
          <h4>
            <CreditCard size={16} /> Créditos de assinatura
          </h4>
          <TableContainer>
            <Table>
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Status</th>
                  <th>Crédito</th>
                  <th>Saldo</th>
                  <th>Motivo</th>
                </tr>
              </thead>
              <tbody>
                {credits.map((credit) => (
                  <tr key={credit.id}>
                    <td>{formatDate(credit.createdAt)}</td>
                    <td>
                      <Badge tone={creditTone(credit.status)}>{label(CREDIT_STATUS_LABELS, credit.status)}</Badge>
                    </td>
                    <td>{formatCurrency(credit.amount)}</td>
                    <td>{formatCurrency(credit.remainingAmount)}</td>
                    <td>{credit.reason ?? <Muted>-</Muted>}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableContainer>
        </Section>
      )}
    </>
  );
};

const AvailabilityTab: React.FC<{ availability: MerchantAvailabilityAnalysisDto | null }> = ({ availability }) => {
  if (!availability) {
    return <Placeholder>Sem dados de disponibilidade.</Placeholder>;
  }

  function checkTone(check: MerchantAvailabilityCheckDto): Tone {
    if (check.passed) return 'success';
    return check.blocking ? 'danger' : 'warning';
  }

  return (
    <Section>
      <h4>
        <Clock3 size={16} /> Disponibilidade da loja
      </h4>
      <FactGrid>
        <Field>
          <div className="k">Status geral</div>
          <div className="v">
            <Badge tone={availability.available ? 'success' : 'danger'}>
              {availability.available ? 'Disponível' : 'Indisponível'}
            </Badge>
          </div>
        </Field>
        <Field>
          <div className="k">Assinatura</div>
          <div className="v">{label(SUBSCRIPTION_STATUS_LABELS, availability.subscriptionStatus)}</div>
        </Field>
        <Field>
          <div className="k">Acesso financeiro</div>
          <div className="v">{availability.subscriptionAccessBlocked ? 'Bloqueado' : 'Liberado'}</div>
        </Field>
        <Field>
          <div className="k">Avaliado em</div>
          <div className="v">{formatDate(availability.evaluatedAt)}</div>
        </Field>
      </FactGrid>

      <div style={{ marginTop: '0.5rem' }}>
        {availability.checks.map((check) => (
          <CheckRow key={check.code}>
            <Dot $tone={checkTone(check)} />
            <div className="body">
              <div className="title">{check.label}</div>
              {check.details && <div className="details">{check.details}</div>}
            </div>
            <Badge tone={checkTone(check)}>{check.passed ? 'OK' : check.blocking ? 'Bloqueia' : 'Atenção'}</Badge>
          </CheckRow>
        ))}
      </div>
    </Section>
  );
};

const AuditTab: React.FC<{ analysis: MerchantBillingAnalysisDto | null }> = ({ analysis }) => {
  const events: PlatformAuditEventDto[] = (analysis?.auditEvents ?? []).slice(0, 20);

  if (events.length === 0) {
    return <Placeholder>Nenhum evento de auditoria registrado.</Placeholder>;
  }

  return (
    <Section>
      <h4>
        <FileText size={16} /> Auditoria administrativa
      </h4>
      <TableContainer>
        <Table>
          <thead>
            <tr>
              <th>Data</th>
              <th>Evento</th>
              <th>Ator</th>
              <th>Agregado</th>
            </tr>
          </thead>
          <tbody>
            {events.map((event) => (
              <tr key={event.id}>
                <td>{formatDate(event.occurredAt)}</td>
                <td>{event.eventType}</td>
                <td>{event.actorId ?? event.actorType ?? <Muted>-</Muted>}</td>
                <td>{event.aggregateType}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </TableContainer>
    </Section>
  );
};

export default MerchantDetailDrawer;
